import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Share2, Trash2, Plus, Megaphone, Home, Pencil, Loader2, MessageCircle, CalendarClock } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { NeonBackground } from "../components/NeonBackground";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const clamp2 = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" };

export default function PromotionsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Rome" });
  const isExpired = (p) => p.expires_at && String(p.expires_at).slice(0, 10) < todayStr;

  const load = () => {
    setLoading(true);
    api.get("/promotions").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const download = async (p) => {
    try {
      const res = await fetch(fileUrl(p.image_path));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(p.title || "locandina").replace(/\s+/g, "-")}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const share = async (p) => {
    const url = fileUrl(p.image_path);
    try {
      if (navigator.share) await navigator.share({ title: p.title, text: p.description, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiato negli appunti"); }
    } catch {}
  };

  const shareWhatsApp = (p) => {
    const url = fileUrl(p.image_path);
    const msg = `Ciao! 👋 Nuova promozione da *AC Creator 3D*:\n\n*${p.title}*${p.description ? "\n" + p.description : ""}\n\n👉 ${url}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const toggleHome = async (p, v) => {
    await api.put(`/promotions/${p.id}`, { show_on_home: v });
    setItems(items.map((x) => (x.id === p.id ? { ...x, show_on_home: v } : x)));
    toast.success(v ? "Mostrata in Home" : "Rimossa dalla Home");
  };

  const remove = async (p) => {
    if (!window.confirm("Eliminare questa locandina?")) return;
    await api.delete(`/promotions/${p.id}`);
    setItems(items.filter((x) => x.id !== p.id));
    toast.success("Locandina eliminata");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/promotions/${editing.id}`, { title: editing.title, description: editing.description, expires_at: editing.expires_at ?? null, regenerate: editing.regenerate });
      setItems(items.map((x) => (x.id === editing.id ? r.data : x)));
      setEditing(null);
      toast.success("Locandina aggiornata");
    } catch {
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const visible = user ? items : items.filter((p) => !isExpired(p));

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <NeonBackground />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-neon-pink flex items-center gap-2"><Megaphone className="w-4 h-4" /> Promozioni</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-white mt-2">Offerte & Locandine</h1>
            <p className="text-muted-foreground font-light mt-2 max-w-xl">Scopri le ultime promozioni di AC Creator 3D. Scarica e condividi le locandine con chi vuoi.</p>
          </div>
          {user && (
            <Link to="/admin/locandina">
              <Button data-testid="promo-create-link" className="bg-neon-pink text-white rounded-sm gap-2 hover:opacity-90"><Plus className="w-4 h-4" /> Crea locandina</Button>
            </Link>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></div>
        ) : visible.length === 0 ? (
          <div data-testid="promo-empty" className="flex flex-col items-center py-24 text-muted-foreground">
            <Megaphone className="w-12 h-12 mb-3 opacity-40" /><p className="font-light">Nessuna promozione al momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p, i) => (
              <motion.div key={p.id} data-testid={`promo-card-${i}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="group bg-card border border-white/10 rounded-sm overflow-hidden hover:border-neon-pink/40 transition-colors duration-200">
                <div className="relative aspect-[4/5] overflow-hidden bg-black">
                  <img src={fileUrl(p.image_path)} alt={p.title} data-testid={`promo-image-${i}`} className="w-full h-full object-cover" loading="lazy" />
                  {user && p.show_on_home && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-neon-green/20 text-neon-green border border-neon-green/40"><Home className="w-3 h-3" /> In Home</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-display font-bold text-lg text-white truncate">{p.title}</h3>
                  {p.description && <p className="text-sm text-muted-foreground font-light mt-1" style={clamp2}>{p.description}</p>}
                  {user && p.expires_at && (
                    <p data-testid={`promo-expiry-${i}`} className={`text-xs font-mono mt-2 flex items-center gap-1 ${isExpired(p) ? "text-neon-pink" : "text-muted-foreground"}`}>
                      <CalendarClock className="w-3 h-3" /> {isExpired(p) ? "Scaduta" : `Scade il ${new Date(p.expires_at).toLocaleDateString("it-IT")}`}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-4">
                    <Button data-testid={`promo-download-${i}`} onClick={() => download(p)} size="sm" variant="outline" className="rounded-sm gap-1.5 border-white/15 flex-1"><Download className="w-4 h-4" /> Scarica</Button>
                    <Button data-testid={`promo-share-${i}`} onClick={() => share(p)} size="sm" variant="outline" className="rounded-sm gap-1.5 border-white/15 flex-1"><Share2 className="w-4 h-4" /> Condividi</Button>
                  </div>
                  <Button data-testid={`promo-whatsapp-${i}`} onClick={() => shareWhatsApp(p)} size="sm" className="w-full mt-2 rounded-sm gap-1.5 bg-[#25D366] text-black hover:bg-[#1ebe5b]"><MessageCircle className="w-4 h-4" /> Condividi su WhatsApp</Button>
                  {user && (
                    <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-white/10">
                      <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch data-testid={`promo-home-${i}`} checked={!!p.show_on_home} onCheckedChange={(v) => toggleHome(p, v)} /> Mostra in Home</label>
                      <div className="flex items-center gap-1">
                        <button data-testid={`promo-edit-${i}`} onClick={() => setEditing({ ...p, regenerate: false })} className="text-muted-foreground hover:text-neon-cyan p-1"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`promo-delete-${i}`} onClick={() => remove(p)} className="text-muted-foreground hover:text-neon-pink p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-card border-white/10 text-white">
          <DialogHeader><DialogTitle>Modifica locandina</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input data-testid="promo-edit-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Titolo" className="bg-input border-white/10 rounded-sm" />
              <Textarea data-testid="promo-edit-desc" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Descrizione" className="bg-input border-white/10 rounded-sm resize-none" />
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Scadenza (facoltativa)</label>
                <Input type="date" data-testid="promo-edit-expiry" value={editing.expires_at ? String(editing.expires_at).slice(0, 10) : ""} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value || null })} className="bg-input border-white/10 rounded-sm mt-1" />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground"><Switch data-testid="promo-edit-regen" checked={editing.regenerate} onCheckedChange={(v) => setEditing({ ...editing, regenerate: v })} /> Rigenera immagine con l'IA</label>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="promo-edit-save" onClick={saveEdit} disabled={saving} className="bg-neon-cyan text-black rounded-sm gap-2 hover:bg-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
